import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, Icon, TableListData } from 'components';
import { Button, useForm, NumberUtils, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import type { GetAllReferralsEntity, ReferralsItem } from 'services/openapi';

import { type FilterType, getInitFilterValues } from './config';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useGetReferralTransactionQuery } from 'services/referral/queries/useGetReferralTransactionQuery';
import { useGetReferralTransactionExportQuery } from 'services/referral/queries/useGetReferralTransactionExportQuery';
import { TransactionStatusColor } from 'configs/constants';
import { useRetrySendReferralPointMutation } from 'services/referral';
import toast from 'react-hot-toast';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';
import { checkValidActionRoleAdmin } from 'utils/helpers';
import { Actions } from 'configs/actions';

const ReferralTransactionsList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const { mutateAsync: retrySendReferralPoint } =
    useRetrySendReferralPointMutation();

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);
  const [lstTxResend, setLstTxResend] = useState<string[]>([]);

  const columnsDefined: ColumnDef<ReferralsItem>[] = [
    {
      accessorKey: 'transactionCode',
      header: 'Mã giao dịch',
    },
    {
      accessorKey: 'referredUserPhoneNumber',
      header: 'Người được giới thiệu',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'referrerPhoneNumber',
      header: 'Người giới thiệu',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'referrerId',
      header: 'Mã giới thiệu',
    },
    {
      accessorKey: 'point',
      header: 'Số tiền',
      cell: (info) => {
        return (
          <div className={'textInfo'}>
            {info.getValue()
              ? NumberUtils.toFormatNumber(Number(info.getValue()))
              : '0'}
          </div>
        );
      },
      // cell: (info) => (info.getValue() ? NumberUtils.toFormatNumber(Number(info.getValue())) : ''),
    },
    {
      accessorKey: 'receivePointDate',
      header: 'Thời gian',
      cell: (info) =>
        format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: (info) => (
        <div
          className={
            TransactionStatusColor[
              info.getValue() as keyof typeof TransactionStatusColor
            ]
          }
        >
          {String(info.getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'message',
      header: 'Lỗi',
    },
    {
      accessorKey: 'actions',
      header: 'Thao tác',
      cell: (info) => (
        <div>
          {info.row.original.status == 'Thất bại' &&
            !lstTxResend.includes(info.row.original.transactionCode) && (
              <div className='action'>
                <div
                  className='action__reject'
                  onClick={async () => {
                    try {
                      setLstTxResend((prev) => [
                        ...prev,
                        info.row.original.transactionCode,
                      ]);
                      await retrySendReferralPoint({
                        transactionCode: info.row.original.transactionCode,
                      });
                      setLstTxResend((prev) =>
                        prev.filter(
                          (tx) => tx !== info.row.original.transactionCode,
                        ),
                      );
                      toast.success('Đã gửi lại giao dịch thành công');
                      handleSubmit();
                    } catch (error: any) {
                      setLstTxResend((prev) =>
                        prev.filter(
                          (tx) => tx !== info.row.original.transactionCode,
                        ),
                      );
                    }
                  }}
                >
                  <span
                    style={{
                      color: 'var(--color-textDanger)',
                    }}
                  >
                    Thử lại
                  </span>
                </div>
              </div>
            )}
          {lstTxResend.includes(info.row.original.transactionCode) && (
            <div className='action'>
              <Icon.Loading />
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [];

  const { handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const convertVariables = (variables: FilterType) => {
    return variables;
  };

  const { isFetching: isLoadingExport, refetch } =
    useGetReferralTransactionExportQuery({
      page: '1',
      pageSize: String((dataTable as unknown as GetAllReferralsEntity)?.total),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const allData = await refetch({ throwOnError: true });
      const summary = allData.data as any;
      await saveListExcel(
        allData.data?.data,
        columnsDefined as ColumnDef<GetAllReferralsEntity>[],
        'Danh_sach_Chuong_Trinh_Gioi_Thieu',
        true,
        {
          totals: {
            referrerId: 'TỔNG',
            point: NumberUtils.toFormatNumber(Number(summary.summary ?? 0)),
          },
        },
      );
    } catch (error) {
      if (!isRequestError(error))
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
    }
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        {checkValidActionRoleAdmin(
          Actions.ManageReferralEvent_ListTransaction,
        ) && (
          <form onSubmit={handleSubmit}>
            <div className='textCenter mt16 wrapButtonFilter'>
              <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
              <Button
                className='ml15'
                onClick={handleExportExcel}
                label={'Tải về DS'}
                type='submit'
                buttonType='info'
                isLoading={isLoadingExport}
              />
            </div>
          </form>
        )}
      </Card>

      <TableListData<GetAllReferralsEntity>
        columns={columnsDefined as ColumnDef<GetAllReferralsEntity>[]}
        useQuery={useGetReferralTransactionQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        orderColumn
        callBackData={setDataTable}
        customTopRow={(data: any) => {
          if (data) {
            return (
              <>
                <tr>
                  <td colSpan={4}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(Number(data?.summary ?? 0))}
                    </strong>
                  </td>
                </tr>
              </>
            );
          }
        }}
      />
    </>
  );
};

export default ReferralTransactionsList;
